import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { colorFor } from "./palette";

/**
 * Stage bars with a running total tracked above them — pitch conversion
 * (won, lost, at contract, at pitch).
 *
 * The bars answer "how many at each stage"; the running total answers "how many
 * have we seen by this stage", which is the number the board asks for next. Win
 * rate is deliberately NOT shown here: it is a closed-only measure, so mixing it
 * into a chart that includes open stages would misstate it.
 */
export default function StageBarChart({ groups = [], runningTotal = [], colors = {}, formatValue, onGroupClick }) {
  const format = formatValue || ((v) => new Intl.NumberFormat("en-GB").format(v));
  const data = groups.map((group, index) => ({
    name: group.label,
    value: group.value,
    running: runningTotal[index],
    fill: colorFor(group.label, index, colors),
  }));

  if (!data.length) {
    return <p className="py-12 text-center text-sm text-[#4a5b68]">No records match this report.</p>;
  }

  const hasRunning = runningTotal.length === groups.length;
  const Chart = hasRunning ? ComposedChart : BarChart;

  return (
    <ResponsiveContainer width="100%" height={320} minWidth={280}>
      <Chart data={data} margin={{ top: 28, right: 16, left: -8, bottom: 0 }}>
        <CartesianGrid stroke="#dddddd" vertical={false} />
        <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fill: "#4a5b68", fontSize: 12 }} />
        <YAxis tickLine={false} axisLine={false} tick={{ fill: "#4a5b68", fontSize: 12 }} tickFormatter={format} />
        <Tooltip
          cursor={{ fill: "rgba(15, 23, 42, 0.04)" }}
          contentStyle={{ borderRadius: 4, borderColor: "#cccccc", boxShadow: "0 4px 12px rgba(51, 71, 91, 0.12)" }}
          formatter={(value, name) => [format(value), name === "running" ? "Running total" : "At this stage"]}
        />
        <Bar
          dataKey="value"
          maxBarSize={56}
          radius={[2, 2, 0, 0]}
          isAnimationActive={false}
          cursor={onGroupClick ? "pointer" : undefined}
          onClick={(entry) => onGroupClick && onGroupClick(entry?.name ?? entry?.payload?.name)}
        >
          {data.map((entry) => (
            <Cell key={entry.name} fill={entry.fill} />
          ))}
          <LabelList dataKey="value" position="insideTop" fill="#ffffff" fontSize={12} fontWeight={700} formatter={format} />
        </Bar>
        {hasRunning && (
          <Line
            type="monotone"
            dataKey="running"
            stroke="#323c44"
            strokeWidth={2}
            dot={{ r: 3, fill: "#323c44" }}
            isAnimationActive={false}
          >
            <LabelList dataKey="running" position="top" fill="#323c44" fontSize={11} fontWeight={700} formatter={format} />
          </Line>
        )}
      </Chart>
    </ResponsiveContainer>
  );
}
